import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type Table,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import {
  type FilterValue,
  type TableDateRange,
  type TableDateRangeFilterConfig,
  type TableFilterConfig,
} from '@avanta/shared/types/table';
import { defaultColumnFilterFn } from '@avanta/shared/utils/table';
import { filterTableData, filterTableDataByDateRange } from './utils';

interface UseClientPaginatedTableProps<T, K extends FilterValue> {
  data?: T[];
  columns: ColumnDef<T>[];
  pageSize?: number;
  initialSorting?: SortingState;
  initialDateRange?: TableDateRange;
  filter?: TableFilterConfig<T, K>;
  onFilterChange?: (value: K | undefined) => void;
  dateRangeFilter?: TableDateRangeFilterConfig<T>;
}

export const useClientPaginatedTable = <T, K extends FilterValue>({
  data = [],
  columns,
  pageSize = 5,
  initialSorting = [],
  initialDateRange,
  filter,
  onFilterChange,
  dateRangeFilter,
}: UseClientPaginatedTableProps<T, K>): {
  table: Table<T>;
  totalCount: number;
  filteredCount: number;
  selectedFilterKey: K | undefined;
  setFilter: (value: K | undefined) => void;
  selectedDateRange: TableDateRange | undefined;
  setDateRange: (value: TableDateRange | undefined) => void;
} => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedFilterKey, setSelectedFilterKey] = useState<K>();
  const [selectedDateRange, setSelectedDateRange] = useState<TableDateRange | undefined>(
    initialDateRange
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });

  const filteredData = useMemo(() => {
    const buttonFilteredData = filterTableData(data, filter, selectedFilterKey);
    return filterTableDataByDateRange(buttonFilteredData, dateRangeFilter, selectedDateRange);
  }, [data, filter, selectedFilterKey, dateRangeFilter, selectedDateRange]);

  const setFilter = useCallback(
    (value: K | undefined) => {
      setSelectedFilterKey(value);
      onFilterChange?.(value);
    },
    [onFilterChange]
  );

  const setDateRange = useCallback((value: TableDateRange | undefined) => {
    setSelectedDateRange(value);
  }, []);

  const table = useReactTable({
    columns,
    data: filteredData,
    defaultColumn: {
      enableSorting: false,
      filterFn: defaultColumnFilterFn,
    },
    state: {
      columnFilters,
      globalFilter,
      sorting,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
  });

  const filteredRowModel = table.getFilteredRowModel();
  const totalCount = filteredData?.length ?? 0;
  const filteredCount = filteredRowModel.rows.length;

  return {
    table,
    totalCount,
    filteredCount,
    selectedFilterKey,
    setFilter,
    selectedDateRange,
    setDateRange,
  };
};
